/* eslint-disable react-hooks/exhaustive-deps */
import BaseCardLayout from "../../components/base-card-layout/base-card-layout";
import { LanguageSelectorUserMenu } from "../../components/language-selector-user-menu/language-selector-user-menu";
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from "react";
import TrialUpgradeFeedbackDialogBox from "../../components/trial-upgrade-feedback-dialog-box/trial-upgrade-feedback-dialog-box";
import { AssessmentsUserMenu } from "../../components/assessments-user-menu/assessments-user-menu";
import { Redirect, useHistory } from "react-router";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import { ReactComponent as GreenCheckmark } from "../../assets/icons/outline/gree-check-mark.svg";
import { ReactComponent as RedCross } from "../../assets/icons/outline/red-cross.svg";
import Plans from "../../components/subscription/plans";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { collection, getDocs, getFirestore, query, where } from "firebase/firestore";
import { Product } from "../../models/interfaces/product";
import { Price } from "../../models/interfaces/price";
import { PaymentCycle } from "../../models/enumerations/payment-cycle";
import { Loader } from "../../components/loader/loader";
import { Modal } from "../../components/modal/modal";
import { PlanIdPlanType } from "../../models/enumerations/plan-id";
import { PlanType } from "../../models/enumerations/plan-type";
import OrganizationSubscriptionService from "../../utilities/services/organization-subscription-service";
import { LicenseLimitPlanType } from "../../models/enumerations/license-limit";
import { PlanTypeLabels } from "../../models/plan-type";
import { UserLimitPlanType } from "../../models/enumerations/user-limit";
import OrganizationService from "../../utilities/services/organization-service";
import { OrganizationStatus } from "../../models/enumerations/organization-status";
import CheckoutFormCreate from "../../components/subscription/checkout-form-create";
import UserRoles from "../../models/user-roles";
import getFirebase from "../../utilities/firebase";
import UserService from "../../utilities/services/user-service";
import { createLogger } from "../../utilities/logger";


const COMPONENT_CLASS = "p-plan-deactivated";
const log = createLogger("PlanDeactivated");

const PlanDeactivated: React.FC = () => {
    const { t } = useTranslation();
    const db = getFirestore();
    const [openCancel, setOpenCancel] = useState(false);
    const { state, setState } = useAuthState();
    const [upgrade, setUpgrade] = useState(false)
    const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);
    const [upgradeModalOpen, setUpgradeModalOpen] = useState<boolean>(false);
    const [products, setProducts] = useState<any[]>();
    const [isLoading, setIsLoading] = useState(true);
    const [selectedProduct, setSelectedProduct] = useState<Product>();
    const [selectedPrice, setSelectedPrice] = useState<Price>();
    const [isUpgrading] = useState(false);
    const [paymentSuccessful, setPaymentSuccessful] = useState<boolean>(false);
    const [, setPaymentFailed] = useState<boolean>(false);
    const [purchasePrice, setPurchasePrice] = useState<number | undefined>();
    const [paymentSuccessModalOpen, setPaymentSuccessModalOpen] = useState<boolean>(false);
    const [isPaymentSuccess] = useState(false);
    const [planType, setPlanType] = useState<PlanType>();
    const { auth } = getFirebase();

    const history = useHistory();
    if (!state.user?.roles?.includes(UserRoles.ACCOUNT_HOLDER_ID)) {
        auth.signOut();
        setState((state) => ({ ...state, ...{ authenticated: false, user: null } }));
        history.replace("/login");
    }

    useEffect(() => {
        const getProductsAndPrices = async () => {
            // Query to get all active products
            const q = query(
                collection(db, 'products'),
                where('active', '==', true)
            );
            const querySnapshot = await getDocs(q);
            // Use Promise.all to fetch all prices in parallel
            const productPromises = querySnapshot.docs.map(async (productDoc) => {
                const prices: any[] = [];
                const pricesCollection = collection(productDoc.ref, 'prices');
                const priceQuerySnapshot = await getDocs(pricesCollection);
                priceQuerySnapshot.docs.forEach((item) => {
                    prices.push({ data: item.data(), id: item.id });
                });

                // Combine product data and its prices
                return {
                    product: { data: productDoc.data(), id: productDoc.id },
                    prices: prices,
                };
            });
            // Wait for all productPromises to complete
            const resolvedProducts = await Promise.all(productPromises);
            setProducts(resolvedProducts);

            setIsLoading(false);
        };

        if (!products || products.length === 0) {
            getProductsAndPrices();
        }
    }, [])

    useEffect(() => {
        const openModal = async () => {
            if (paymentSuccessful) {
                await updateOrg();
                setUpgradeModalOpen(false);
                setPaymentSuccessModalOpen(true);
                setPaymentSuccessful(false);
            }
        }
        openModal();

    }, [paymentSuccessful, products])

    if (!state.authenticated) {
        return <Redirect to="/login" />;
    }

    const updateOrg = async () => {
        if (!state.organization.id) {
            log.warn("updateOrg aborted - no organization id", { planType });
            // Couldn't get organization id
            return;
        }

        try {
            const o = await OrganizationService.get(state.organization.id);
            const temp = await OrganizationSubscriptionService.getByOrganizationId(state.organization.id);
            const oSub = temp[0];

            if (!(o && oSub && oSub.stripePlanSubscription && planType)) {
                log.warn("updateOrg gate failed - skipping activation", {
                    organizationId: state.organization.id,
                    organizationName: o?.name,
                    hasOrg: !!o,
                    hasOrgSub: !!oSub,
                    hasStripePlanSubscription: !!oSub?.stripePlanSubscription,
                    hasPlanType: !!planType,
                    planType,
                    orgSubIds: temp.map((sub) => sub.id),
                    source: "plan-deactivated-updateOrg",
                });
                return;
            }

            const previousStatus = o.status;
            o.plan = { value: planType, label: PlanTypeLabels[planType] };
            o.userLimit = UserLimitPlanType[planType];
            if (o.userCount && o.userCount > o.userLimit) {
                await UserService.disableAllUsers(o, state.user);
            }
            o.mySymmioLicense = LicenseLimitPlanType[planType];
            if (o.mySymmioLicenseCount && o.mySymmioLicenseCount > o.mySymmioLicense) {
                await UserService.removeWellnessAllUsers(o, state.user);
            }
            o.status = OrganizationStatus.Active;
            o.showManuallyDeactivatedAccountModal = false;

            await OrganizationService.save(o);
            setState({ ...state, organization: o })

            oSub.userLimit = UserLimitPlanType[planType];
            oSub.price = purchasePrice;
            oSub.plan!.label = PlanTypeLabels[planType];
            oSub.plan!.value = planType;
            if (!oSub.paymentCycle) {
                oSub.paymentCycle = {
                    label: PaymentCycle[selectedPrice?.interval === "month" ? PaymentCycle.Monthly : PaymentCycle.Yearly],
                    value: selectedPrice?.interval === "month" ? PaymentCycle.Monthly : PaymentCycle.Yearly
                }
            }
            else {
                oSub.paymentCycle!.label = PaymentCycle[selectedPrice?.interval === "month" ? PaymentCycle.Monthly : PaymentCycle.Yearly];
                oSub.paymentCycle!.value = selectedPrice?.interval === "month" ? PaymentCycle.Monthly : PaymentCycle.Yearly;
            }

            oSub.mySymmioLicense = LicenseLimitPlanType[planType];

            await OrganizationSubscriptionService.save(oSub);

            log.warn("Organization status changed", {
                organizationId: o.id,
                organizationName: o.name,
                fromStatus: OrganizationStatus[Number(previousStatus)] ?? previousStatus,
                fromStatusValue: previousStatus,
                toStatus: OrganizationStatus[Number(o.status)] ?? o.status,
                toStatusValue: o.status,
                orgSubId: oSub.id,
                stripePlanSubscription: oSub.stripePlanSubscription,
                planType,
                purchasePrice,
                source: "plan-deactivated-updateOrg",
            });
        } catch (err) {
            log.error("updateOrg failed", {
                exception: err,
                organizationId: state.organization.id,
                planType,
                source: "plan-deactivated-updateOrg",
            });
            throw err;
        }
    }

    const handleProductSelect = async (data: any) => {
        if (products && products.length !== 0) {
            const product = products?.find((item) => item.product.id === data.id)
            setSelectedProduct(product.product.data)

            const tempSelectedPrice = product.prices.find((item: any) => data.interval === PaymentCycle.Monthly ? item.data.interval === "month" : item.data.interval !== "month");

            setSelectedPrice({ ...tempSelectedPrice.data, id: tempSelectedPrice.id });

            setPlanType(PlanIdPlanType[product.product.id]);

            setPurchasePrice((tempSelectedPrice && tempSelectedPrice.data.unit_amount !== undefined) ? (tempSelectedPrice.data.unit_amount / 100) : undefined);

            setUpgradeModalOpen(true);
        }

    }

    return (
        <>
            <Loader
                isVisible={isLoading} />
            <div className="user-menu flex-grow">
                <AssessmentsUserMenu profileImage={false} />
                <LanguageSelectorUserMenu userPortal={true} />
            </div>
            <Modal
                isOpen={paymentSuccessModalOpen}
                isLoading={isPaymentSuccess}
                onClose={() => {
                    setPaymentSuccessModalOpen(false);
                    history.push(`/`);
                }}
                defaultCenterModal={true}
                submitButtonText={t('buttons.btn_continue')}
                onSubmit={() => {
                    setPaymentSuccessModalOpen(false);
                    history.push(`/`);
                }}
                title={t('billing.planUpgraded')}>
                <div className={`${COMPONENT_CLASS}__modal-wrapper`}>
                    <div className={`${COMPONENT_CLASS}__modal-content`}>
                        <p><b>{t('billing.thankYou')}</b></p>
                        <p>{t('billing.emailConfirmation')}</p>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={upgradeModalOpen}
                isLoading={isUpgrading}
                onClose={() => {
                    setUpgradeModalOpen(false);
                }}
                title={t('billing.upgradePlan')}>
                {stripePromise && products && products.length > 0 && selectedProduct &&
                    <>
                        <Elements stripe={stripePromise} options={{ mode: 'setup', currency: 'usd' }}>
                            <CheckoutFormCreate
                                setUpgradeModalOpen={setUpgradeModalOpen}
                                paymentSuccessful={setPaymentSuccessful}
                                paymentFailed={setPaymentFailed}
                                onChangePlan={() => { setUpgradeModalOpen(false); }}
                                returnUrl={`/settings/upgrade`}
                                paymentCycle={
                                    selectedPrice
                                        ? selectedPrice.interval === "month"
                                            ? PaymentCycle.Monthly : PaymentCycle.Yearly
                                        : undefined
                                }
                                selectedProduct={selectedProduct}
                                selectedPrice={selectedPrice}
                                products={products}
                                isCreating={false}
                                isLoading={isLoading}
                                setIsLoading={setIsLoading}
                            />
                        </Elements>
                    </>
                }

            </Modal>

            <BaseCardLayout>
                <div className={`${COMPONENT_CLASS}`}>
                    {upgrade ?
                        <div>
                            <div className="md:px-16 px-8 pt-10">
                                <Plans
                                    onProductSelect={handleProductSelect}
                                    isUpgrade={true} />
                            </div>
                        </div> :
                        <div className="w-full">
                            <header className="pt-20 px-4 md:px-16">
                                <h1 className="text-center">{t('planDeactivedPage.ended')}</h1>
                                <p className="text-center">
                                    {t('planDeactivedPage.subTitle1')}
                                </p>
                                <p className="text-center">
                                    {t('planDeactivedPage.subTitle2')}
                                </p>
                            </header>
                            <div className="container">
                                <div className="card-container" onClick={() => { setUpgrade(true) }}>
                                    <div className="icon-container"><GreenCheckmark /></div>
                                    <div className="card-text">
                                        <h2>{t('planDeactivedPage.reactivateTitle')}</h2>
                                        <p>{t('planDeactivedPage.reactivateSubtitle')}</p>
                                    </div>
                                </div>
                                <div className="card-container" onClick={() => setOpenCancel(true)}>
                                    <div className="icon-container"><RedCross /></div>
                                    <div className="card-text">
                                        <h2>{t('planDeactivedPage.declineTitle')}</h2>
                                        <p>{t('planDeactivedPage.declineSubtitle')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    }
                </div>

            </BaseCardLayout>

            <TrialUpgradeFeedbackDialogBox
                isOpen={openCancel}
                onClose={setOpenCancel}
                title={t('trialUpgradeFeedbackDialogBox.title')}
            />
        </>
    );
};

export default PlanDeactivated;
